import { ResidentItemHeader } from '@/components/residentItemHeader';
import { color } from '@/styles';
import { IResident } from '@/types/api/traversal/getNursingHomeResidents';
import { Button } from '@/components/mui/button';
import { OutlineButton } from '@/components/outlineButton';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import dayjs, { locale } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { POSTURES } from '@/constants/activityStatus';
import api from '@/api';
import { IGetPostureRes } from '@/types/api/dfree-devices/getPosture';
import { batteryStatusStr } from '@/utils/batteryStatus';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(duration);

interface Props {
  resident: IResident;
  handleClickRecord: () => void;
}

const HeaderRightContainer = css`
  display: flex;
  flex-wrap: wrap-reverse;
  align-items: center;
  justify-content: end;
  min-height: 48px;
  gap: 8px;
`;

const DeviceInfoContainer = css`
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 25px 62px;
  align-items: center;
  align-content: center;
  column-gap: 8px;
`;

const SerialNumber = (textColor: string) => css`
  font-size: 14px;
  color: ${textColor};
  display: flex;
  align-items: center;
  column-gap: 6px;
`;

const NotificationSettingIcons = css`
  display: flex;
  align-items: center;
  column-gap: 5px;

  > img {
    width: 17px;
    height: auto;
  }
`;

const RecordButton = styled(Button)`
  border-radius: 24px;
  padding: 8px 24px;
`;

const HeaderMiddle = css`
  display: flex;
  column-gap: 7px;
`;

const PostureContainer = css`
  min-width: 170px;
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

const PostureIcon = (isDisplay: boolean) => css`
  display: ${isDisplay ? 'block' : 'none'};
`;

const PostureText = (size: number, weight?: string) => css`
  font-size: ${` ${size}px`};
  font-weight: ${weight ?? 'normal'};
  line-height: 1;
`;

const PostureTextContainer = css`
  display: flex;
  flex-direction: column;
  row-gap: 6px;
  font-size: 12px;
`;

const ContentGap = css`
  display: flex;
  gap: 8px;
`;

const NextCareTimingInfo = (colorCode?: string) => css`
  position: relative;
  padding-left: 16px;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    width: 4px;
    height: 100%;
    background-color: ${colorCode ? colorCode : 'inherit'};
    border-radius: 4px;
  }
`;

const NextCareTimeText = (colorCode?: string) => css`
  font-size: 16px;
  line-height: 18px;
  font-weight: bold;
  color: ${colorCode ? colorCode : 'inherit'};
`;

const DfreeGraphListItemHeader = ({ resident, handleClickRecord }: Props) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  locale(i18n.language);

  const [isDeviceConnected, setIsDeviceConnected] = React.useState<boolean>(resident.urineSize !== null);
  const [isBDeviceConnected, setIsBDeviceConnected] = React.useState<boolean>(resident.gasPercentage !== null);

  React.useEffect(() => {
    setIsDeviceConnected(resident.urineSize !== null);
    setIsBDeviceConnected(resident.gasPercentage !== null);
  }, [resident.urineSize, resident.gasPercentage]);

  const handleGoDetail = React.useCallback(() => {
    history.push(`./residents/${resident.id}/graph`);
  }, []);

  const handleGoEdit = React.useCallback(() => {
    history.push(`./residents/${resident.id}/edit`);
  }, []);

  return (
    <ResidentItemHeader
      name={resident.name}
      unitName={resident.nursingHomeUnitName}
      roomName={resident.roomName}
      memo={resident.carePlanMemo}
      middleElement={
        <div css={HeaderMiddle}>
          <Posture
            residentId={resident.id}
            posture={resident.posture}
            postureDuration={resident.postureDuration}
            isConnected={isDeviceConnected}
            deviceType={resident.dfreeDeviceType}
          />
          <CareTime
            nextCareTiming={resident.nextCareTiming}
            isCareCompleted={resident.isCareCompleted}
            inCareTiming={resident.inCareTiming}
          />
        </div>
      }
      rightElement={
        <div css={HeaderRightContainer}>
          <DeviceInfo
            serialNumber={resident.serialNumber}
            gasSerialNumber={resident.gasSerialNumber}
            batteryRemaining={resident.batteryRemaining}
            gasBatteryRemaining={resident.gasBatteryRemaining}
            isDeviceConnected={isDeviceConnected}
            isBDeviceConnected={isBDeviceConnected}
            bigBladderNotificationSetting={resident.bigBladderNotificationSetting}
            urinatedNotificationSetting={resident.urinatedNotificationSetting}
            gotUpNotificationSettging={resident.gotUpNotificationSettging}
            gasPoopedNotificationSetting={resident.gasPoopedNotificationSetting}
            gasPredictionNotificationSetting={resident.gasPredictionNotificationSetting}
          />
          <OutlineButton
            onClick={handleGoDetail}
            icon={<img src='/images/icons/graph.svg' alt={t('analysis', '履歴・分析')} />}
            bgColor={color.white}
          >
            {t('analysis', '履歴・分析')}
          </OutlineButton>
          <OutlineButton
            onClick={handleGoEdit}
            icon={<img src='/images/icons/resident-setting.svg' alt='入居者設定' />}
            bgColor={color.white}
          >
            {t('edit', '編集')}
          </OutlineButton>
          <RecordButton variant='contained' onClick={handleClickRecord}>
            {t('recordButton', '記録')}
          </RecordButton>
        </div>
      }
    />
  );
};

interface PostureProps {
  residentId: IResident['id'];
  posture: IResident['posture'];
  postureDuration: IResident['postureDuration'];
  isConnected: boolean;
  deviceType: string | null;
}

type CurrentPosture = {
  posture: number;
  timestamp: string;
};

const Posture = ({ residentId, posture, postureDuration, isConnected, deviceType }: PostureProps) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { t, i18n } = useTranslation();
  locale(i18n.language);
  // 表示用の変数
  const [currentPosture, setCurrentPosture] = React.useState<CurrentPosture | null>(null);
  // 最新姿勢APIからデータを格納
  const [latestPosture, setLatestPosture] = React.useState<CurrentPosture | null>(null);
  // TOP用APIからのデータを格納
  const [topPosture, setTopPosture] = React.useState<CurrentPosture | null>(null);

  const [postureDurationText, setPostureDurationText] = React.useState<string>('');

  const isChangedPosture = (pos1: number, pos2: number) => {
    // UP2では0→1,1→0の変化を姿勢変化とみなさない
    if (deviceType === 'u2p' && ((pos1 === 1 && pos2 === 0) || (pos1 === 0 && pos2 === 1))) {
      return false;
    }
    return pos1 !== pos2;
  };

  const fetchPosture = React.useCallback(async () => {
    const result: IGetPostureRes[] = await api.get('/nursing-homes/:nursingHomeId/dfree-devices/stats/posture', {
      params: { nursingHomeId, residentId },
    });

    // データが存在しているか
    // result[0]?.postureやlatestPosture?.postureは0の場合もあるので、!!での判定はしない
    if (!!result && typeof result[0]?.posture === 'number' && !!result[0]?.timestamp) {
      if (!latestPosture) {
        setLatestPosture({ posture: result[0].posture, timestamp: dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') });
        return;
      }
      // 直前の値と異なる場合
      if (typeof latestPosture?.posture === 'number' && isChangedPosture(result[0].posture, latestPosture?.posture)) {
        setLatestPosture({ posture: result[0].posture, timestamp: result[0]?.timestamp });
        return;
      }
    }
  }, [nursingHomeId, residentId, latestPosture, isChangedPosture]);

  // 表示する経過時間を更新
  const updatePostureDurationText = React.useCallback(() => {
    const postureDurationDiffMs = dayjs().utc().diff(dayjs.utc(currentPosture?.timestamp), 'milliseconds');

    if (postureDurationDiffMs >= 12 * 60 * 60 * 1000) {
      setPostureDurationText(`${t('over12hr', '12時間以上')} ${t('elapsed', '経過')}`);
      return;
    }
    const duration = dayjs.duration(postureDurationDiffMs);
    setPostureDurationText(
      `${duration.hours()}${t('Hr', '時間')}${duration.minutes()}${t('Min', '分')} ${t('elapsed', '経過')}`
    );
  }, [currentPosture]);

  // 5秒間隔で最新姿勢を取得する
  React.useEffect(() => {
    updatePostureDurationText();
    const timer = setInterval(() => {
      if (isConnected) {
        fetchPosture();
        updatePostureDurationText();
      }
    }, 5000);
    return () => {
      clearInterval(timer);
    };
  }, [updatePostureDurationText, fetchPosture, isConnected]);

  // 切断されたら状態を初期化する
  React.useEffect(() => {
    if (!isConnected) {
      setCurrentPosture(null);
      setLatestPosture(null);
      setTopPosture(null);
      updatePostureDurationText();
    }
  }, [isConnected]);

  // APIからのデータを見て最新状態に更新する
  React.useEffect(() => {
    // TOP用APIからのデータをチェック
    if (posture !== null && postureDuration !== null) {
      //  TOP用APIからの姿勢情報が未設定
      if (!topPosture || typeof topPosture?.posture !== 'number' || !topPosture.timestamp) {
        setTopPosture({ posture: posture, timestamp: postureDuration });
      }

      // 姿勢情報が未設定
      if (!currentPosture || typeof currentPosture.posture !== 'number' || !currentPosture.timestamp) {
        setCurrentPosture({ posture: posture, timestamp: postureDuration });
        return;
      }

      // 姿勢が変わったとき、姿勢情報を更新（TOP用APIからのデータ正とする）
      // 前回TOP用APIで取得した値と異なり、かつ表示中のものとも異なる場合
      // 表示中のものと比較するとき、姿勢は同じだけど経過時間が異なる場合もデータ更新
      if (
        typeof topPosture?.posture === 'number' &&
        isChangedPosture(posture, topPosture?.posture) &&
        (isChangedPosture(posture, currentPosture.posture) || postureDuration !== currentPosture.timestamp)
      ) {
        setCurrentPosture({ posture: posture, timestamp: postureDuration });
        setTopPosture({ posture: posture, timestamp: postureDuration });
        return;
      }
    }

    // 最新姿勢APIからのデータをチェック：latestPosture.timestampは取得ごとに変化するので、postureが変わったかのみチェック
    if (!!latestPosture && latestPosture?.posture !== null && latestPosture?.timestamp !== null) {
      // 姿勢情報が未設定
      if (!currentPosture || typeof currentPosture.posture !== 'number' || !currentPosture.timestamp) {
        setCurrentPosture({ posture: latestPosture.posture, timestamp: latestPosture.timestamp });
        return;
      }

      // 姿勢が変わったとき、姿勢情報を更新
      if (isChangedPosture(latestPosture.posture, currentPosture.posture)) {
        setCurrentPosture({ posture: latestPosture.posture, timestamp: latestPosture.timestamp });
        return;
      }
    }
  }, [posture, postureDuration, latestPosture?.posture]);

  const isDisplayPosture: boolean = React.useMemo(() => {
    return isConnected && (deviceType === 'u2' || deviceType === 'u2p') && !!currentPosture;
  }, [deviceType, isConnected, currentPosture]);

  const posturesInfo = React.useMemo(() => {
    if (deviceType === 'u2' || deviceType === 'u2p') {
      return POSTURES[deviceType];
    }
    return null;
  }, [deviceType]);

  const postureInfo = React.useMemo(() => {
    if (isDisplayPosture && (deviceType === 'u2' || deviceType === 'u2p')) {
      return posturesInfo?.find((v) => v.id === currentPosture?.posture);
    }
  }, [deviceType, currentPosture?.posture]);

  return (
    <div css={PostureContainer} data-testid='posture'>
      {isDisplayPosture ? (
        <>
          {posturesInfo?.map((v) => (
            <img key={v.id} src={v.iconPath} alt={t(v.nameKey, v.name)} css={PostureIcon(v.id === postureInfo?.id)} />
          ))}
          <img src='/images/residents/posture/none.svg' alt='-' css={PostureIcon(!postureInfo)} />
          <div css={PostureTextContainer}>
            {postureInfo ? (
              <>
                <span css={PostureText(12)}>{t(postureInfo?.nameKey, postureInfo?.name)}</span>
                <span css={PostureText(14)}>{currentPosture?.timestamp && postureDurationText}</span>
              </>
            ) : (
              <span css={PostureText(12, 'bold')}>-</span>
            )}
          </div>
        </>
      ) : (
        <>
          <img src='/images/residents/posture/none.svg' alt='-' />
          <span css={PostureText(12)}>-</span>
        </>
      )}
    </div>
  );
};

interface CareTimeProps {
  nextCareTiming: IResident['nextCareTiming'];
  isCareCompleted: IResident['isCareCompleted'];
  inCareTiming: IResident['inCareTiming'];
}

const CareTime = React.memo(({ nextCareTiming, isCareCompleted, inCareTiming }: CareTimeProps) => {
  const { t, i18n } = useTranslation();
  locale(i18n.language);

  const nextCareDatetime = React.useMemo(() => {
    if (!nextCareTiming) return;
    const localTime = dayjs.utc(nextCareTiming).local();
    const now = dayjs();

    if (localTime.isSame(now, 'day')) {
      return { day: t('Today', '本日'), time: localTime.format('HH:mm') };
    } else if (localTime.isSame(now.subtract(1, 'day'), 'day')) {
      return { day: t('Yesterday', '昨日'), time: localTime.format('HH:mm') };
    } else if (localTime.isSame(now.add(1, 'day'), 'day')) {
      return { day: t('Tomorrow', '明日'), time: localTime.format('HH:mm') };
    } else {
      const [day, time] = localTime.format('YYYY/MM/DD HH:mm').split(' ');
      // dayは未使用
      return { day, time };
    }
  }, [nextCareTiming]);

  const textColor = React.useMemo(() => {
    if (isCareCompleted === 0 && inCareTiming === 1) {
      return color.vermilion;
    } else if (isCareCompleted === 0 && inCareTiming === 0) {
      return color.dark_gray;
    } else if (isCareCompleted === 1) {
      return color.gray;
    }
  }, [inCareTiming, isCareCompleted]);

  return (
    <>
      {nextCareDatetime != null && !(isCareCompleted == null || inCareTiming == null) && (
        <div css={NextCareTimingInfo(textColor)} data-testid='next-care-timing'>
          <div css={ContentGap}>
            <span>{t('NextToiletingCare', '次の排泄ケア')}</span>
          </div>
          <div css={ContentGap}>
            <span css={NextCareTimeText(textColor)}>{nextCareDatetime?.time}</span>
          </div>
        </div>
      )}
    </>
  );
});

interface DeviceInfoProps {
  serialNumber: IResident['serialNumber'];
  gasSerialNumber: IResident['gasSerialNumber'];
  batteryRemaining: IResident['batteryRemaining'];
  gasBatteryRemaining: IResident['gasBatteryRemaining'];
  isDeviceConnected: boolean;
  isBDeviceConnected: boolean;
  bigBladderNotificationSetting: IResident['bigBladderNotificationSetting'];
  urinatedNotificationSetting: IResident['urinatedNotificationSetting'];
  gotUpNotificationSettging: IResident['gotUpNotificationSettging'];
  gasPoopedNotificationSetting: IResident['gasPoopedNotificationSetting'];
  gasPredictionNotificationSetting: IResident['gasPredictionNotificationSetting'];
}

const DeviceInfo = React.memo(
  ({
    serialNumber,
    gasSerialNumber,
    batteryRemaining,
    gasBatteryRemaining,
    isDeviceConnected,
    isBDeviceConnected,
    bigBladderNotificationSetting,
    urinatedNotificationSetting,
    gotUpNotificationSettging,
    gasPoopedNotificationSetting,
    gasPredictionNotificationSetting,
  }: DeviceInfoProps) => {
    const { t, i18n } = useTranslation();
    locale(i18n.language);

    const [deviceBatteryStatus, setDeviceBatteryStatus] = React.useState<string>(
      batteryStatusStr(batteryRemaining, isDeviceConnected)
    );
    const [bDeviceBatteryStatus, setBDeviceBatteryStatus] = React.useState<string>(
      batteryStatusStr(gasBatteryRemaining, isBDeviceConnected)
    );

    React.useEffect(() => {
      setDeviceBatteryStatus(batteryStatusStr(batteryRemaining, isDeviceConnected));
      setBDeviceBatteryStatus(batteryStatusStr(gasBatteryRemaining, isBDeviceConnected));
    }, [batteryRemaining, gasBatteryRemaining, isDeviceConnected, isBDeviceConnected]);

    return (
      <div css={DeviceInfoContainer} data-testid='device-info'>
        {serialNumber !== null ? (
          <>
            {deviceBatteryStatus === '3' ? (
              <div css={SerialNumber(color.red)}>
                <img src={'/images/residents/valid-device.svg'} alt='DFreeデバイス' />
                {t('charge', '要充電')}
              </div>
            ) : (
              <div css={SerialNumber(color.dark_gray)}>
                <img src={'/images/residents/valid-device.svg'} alt='DFreeデバイス' />
                {serialNumber}
              </div>
            )}
            <div>
              {batteryRemaining != null && (
                <img src={`/images/residents/battery-${deviceBatteryStatus}.svg`} alt='DFreeデバイスバッテリー残量' />
              )}
            </div>
            <div css={NotificationSettingIcons}>
              <img
                src={
                  '/images/residents/' + (bigBladderNotificationSetting ? 'on-bigbradder.svg' : 'off-bigbradder.svg')
                }
                alt='そろそろ通知'
              />
              <img
                src={'/images/residents/' + (urinatedNotificationSetting ? 'on-urination.svg' : 'off-urination.svg')}
                alt='出たかも通知'
              />
              <img
                src={'/images/residents/' + (gotUpNotificationSettging ? 'on-gotup.svg' : 'off-gotup.svg')}
                alt='起き上がり通知'
              />
            </div>
          </>
        ) : (
          <>
            <div css={SerialNumber(color.border_gray_dark)}>
              <img src={'/images/residents/invalid-device.svg'} alt='DFreeデバイス' />-
            </div>
            {/* gridで3個目の要素で折り返すようにしているので、要素がない場合もdomに配置しておく（DeviceInfoContainer参照） */}
            <div></div>
            <div></div>
          </>
        )}

        {gasSerialNumber !== null ? (
          <>
            {bDeviceBatteryStatus === '3' ? (
              <div css={SerialNumber(color.red)}>
                <img src={'/images/residents/valid-b-device.svg'} alt='DFreeBenデバイス' />
                {t('charge', '要充電')}
              </div>
            ) : (
              <div css={SerialNumber(color.dark_gray)}>
                <img src={'/images/residents/valid-b-device.svg'} alt='DFreeBenデバイス' />
                {gasSerialNumber}
              </div>
            )}
            <div>
              {gasBatteryRemaining != null && (
                <img
                  src={`/images/residents/battery-${bDeviceBatteryStatus}.svg`}
                  alt='DFreeBenデバイスバッテリー残量'
                />
              )}
            </div>
            <div css={NotificationSettingIcons}>
              <img
                src={'/images/residents/' + (gasPoopedNotificationSetting ? 'on-gas-pooped.svg' : 'off-gas-pooped.svg')}
                alt='臭い検知通知'
              />
              <img
                src={
                  '/images/residents/' +
                  (gasPredictionNotificationSetting ? 'on-gas-prediction.svg' : 'off-gas-prediction.svg')
                }
                alt='排便予測時間通知'
              />
            </div>
          </>
        ) : (
          <>
            <div css={SerialNumber(color.border_gray_dark)}>
              <img src={'/images/residents/invalid-b-device.svg'} alt='DFreeBenデバイス' />-
            </div>
            {/* gridで3個目の要素で折り返すようにしているので、要素がない場合もdomに配置しておく（DeviceInfoContainer参照） */}
            <div></div>
            <div></div>
          </>
        )}
      </div>
    );
  }
);

export default DfreeGraphListItemHeader;
